import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Button,
  Space,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Table,
  Row,
  Col,
  Typography,
  App,
  Spin,
  Alert,
  Divider,
  Descriptions,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/services/api.service";
import supplierAdvanceRefundService, {
  REFUND_CHARGE_TYPES,
  computeFxPreview,
  type CreateSupplierAdvanceRefundPayload,
  type RefundChargeLineDTO,
  type SupplierAdvanceRecoveryExposure,
} from "@/services/supplierAdvanceRefund.service";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useChartAccountsByType } from "@/hooks/useSalesInvoice";
import EntityAttachmentsCard from "@/components/documents/EntityAttachmentsCard";
import {
  DOCUMENT_ENTITY_TYPE_SUPPLIER_ADVANCE_REFUND_VOUCHER,
  SCM_ATTACHMENT_UPLOAD_HINT,
} from "@/constants/document-entity-types";

const { Title, Text } = Typography;
const { TextArea } = Input;

const LIST_PATH = "/finance/ap/supplier-advance-refunds";

function unwrap<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return ((data as { data?: T })?.data ?? data) as T;
}

type ChargeRow = RefundChargeLineDTO & { key: string };

const SupplierAdvanceRefundFormPage: React.FC = () => {
  const { t } = useTranslation("finance");
  const { t: tc } = useTranslation("common");
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const exposureId = searchParams.get("exposure_id");
  const sarIdParam = searchParams.get("supplier_advance_request_id");
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const isEdit = !!id && id !== "new";
  const isNew = !isEdit;

  const [form] = Form.useForm();
  const [chargeRows, setChargeRows] = useState<ChargeRow[]>([]);
  const [context, setContext] = useState<{
    supplier_name?: string;
    po_number?: string;
    advance_request_number?: string;
    original_advance: number;
    already_refunded: number;
    already_adjusted: number;
    refund_available: number;
    advance_currency: string;
    recovery_exposure_id?: string;
    supplier_advance_request_id?: string;
  } | null>(null);

  const {
    baseCurrency,
    allCurrencies,
    loading: currenciesLoading,
  } = useCurrency();
  const currencyOptions = useMemo(
    () =>
      allCurrencies.map((c) => ({
        value: c.currency_code,
        label: `${c.currency_code} - ${c.currency_name}`,
      })),
    [allCurrencies],
  );

  const refundAmount = Form.useWatch("refund_amount", form) as
    | number
    | undefined;
  const bankReceived = Form.useWatch("bank_received_amount", form) as
    | number
    | undefined;
  const advanceRate = Form.useWatch("advance_exchange_rate", form) as
    | number
    | undefined;
  const refundRate = Form.useWatch("refund_exchange_rate", form) as
    | number
    | undefined;
  const refundCurrency = Form.useWatch("refund_currency", form) as
    | string
    | undefined;

  const { data: voucher, isLoading: voucherLoading } = useQuery({
    queryKey: ["supplier-advance-refund", id],
    queryFn: () => supplierAdvanceRefundService.findById(id!),
    enabled: isEdit,
  });

  const { data: exposure, isLoading: exposureLoading } = useQuery({
    queryKey: ["supplier-advance-recovery-exposure", exposureId],
    queryFn: () => supplierAdvanceRefundService.findExposureById(exposureId!),
    enabled: isNew && !!exposureId,
  });

  const { data: assetAccounts = [] } = useChartAccountsByType("Asset", false);
  const { data: expenseAccounts = [] } = useChartAccountsByType(
    "Expense",
    false,
  );

  const { data: allAccounts = [] } = useQuery({
    queryKey: ["chart-of-accounts-all-refund"],
    queryFn: async () => {
      const res = await api.get("/finance/chart-of-accounts");
      const list = unwrap<any[]>(res) ?? [];
      return Array.isArray(list) ? list.filter((a: any) => !a.is_group) : [];
    },
  });

  const bankAccountOptions = useMemo(
    () =>
      assetAccounts.map((a: any) => ({
        value: a.id,
        label: `${a.account_code} — ${a.account_name}`,
      })),
    [assetAccounts],
  );

  const advanceGlOptions = useMemo(
    () =>
      assetAccounts.map((a: any) => ({
        value: a.id,
        label: `${a.account_code} — ${a.account_name}`,
      })),
    [assetAccounts],
  );

  const chargeAccountOptions = useMemo(
    () =>
      [...expenseAccounts, ...assetAccounts].map((a: any) => ({
        value: a.id,
        label: `${a.account_code} — ${a.account_name}`,
      })),
    [expenseAccounts, assetAccounts],
  );

  const fxGainLossOptions = useMemo(
    () =>
      allAccounts
        .filter((a: any) =>
          /exchange|forex|fx/i.test(`${a.account_name} ${a.account_code}`),
        )
        .map((a: any) => ({
          value: a.id,
          label: `${a.account_code} — ${a.account_name}`,
        })),
    [allAccounts],
  );

  const chargeTotal = useMemo(
    () =>
      chargeRows.reduce((s, r) => s + Math.max(0, Number(r.amount) || 0), 0),
    [chargeRows],
  );

  const fxPreview = useMemo(() => {
    if (refundAmount == null || bankReceived == null) return null;
    return computeFxPreview(
      Number(refundAmount) || 0,
      Number(bankReceived) || 0,
      Number(advanceRate) || 1,
      Number(refundRate) || 1,
    );
  }, [refundAmount, bankReceived, advanceRate, refundRate]);

  const applyExposureContext = (exp: SupplierAdvanceRecoveryExposure) => {
    setContext({
      supplier_name: exp.supplier_name ?? exp.supplier?.supplier_name,
      po_number: exp.po_number,
      advance_request_number: exp.advance_request_number,
      original_advance: Number(exp.advance_paid),
      already_refunded: Number(exp.advance_refunded),
      already_adjusted: Number(exp.advance_adjusted),
      refund_available: Number(exp.remaining_refundable_balance),
      advance_currency: exp.currency,
      recovery_exposure_id: exp.id,
      supplier_advance_request_id: exp.supplier_advance_request_id,
    });
    form.setFieldsValue({
      refund_date: dayjs(),
      advance_currency: exp.currency,
      refund_currency: exp.currency,
      advance_exchange_rate: 1,
      refund_exchange_rate: 1,
      refund_amount: Number(exp.remaining_refundable_balance),
      bank_received_amount: Number(exp.remaining_refundable_balance),
    });
  };

  useEffect(() => {
    if (exposure) applyExposureContext(exposure);
  }, [exposure]);

  useEffect(() => {
    if (voucher && isEdit) {
      if (voucher.status !== "Draft") {
        message.warning(t("onlyDraftRefundsEditable"));
        navigate(`${LIST_PATH}/${voucher.id}`);
        return;
      }
      setContext({
        supplier_name: voucher.supplier?.supplier_name,
        po_number: voucher.purchaseOrder?.po_number,
        advance_request_number:
          voucher.supplierAdvanceRequest?.advance_request_number,
        original_advance: Number(voucher.original_advance_amount),
        already_refunded: Number(voucher.already_refunded),
        already_adjusted: Number(voucher.already_adjusted),
        refund_available: Number(voucher.refund_available),
        advance_currency: voucher.advance_currency,
        recovery_exposure_id: voucher.recovery_exposure_id,
        supplier_advance_request_id: voucher.supplier_advance_request_id,
      });
      form.setFieldsValue({
        refund_date: voucher.refund_date ? dayjs(voucher.refund_date) : dayjs(),
        refund_amount: Number(voucher.refund_amount),
        bank_received_amount: Number(voucher.bank_received_amount),
        receiving_account_id: voucher.receiving_account_id,
        advance_gl_account_id: voucher.advance_gl_account_id,
        advance_currency: voucher.advance_currency,
        refund_currency: voucher.refund_currency,
        advance_exchange_rate: Number(voucher.advance_exchange_rate ?? 1),
        refund_exchange_rate: Number(voucher.refund_exchange_rate ?? 1),
        exchange_gain_loss_account_id: voucher.exchange_gain_loss_account_id,
        reference_number: voucher.reference_number,
        narration: voucher.narration,
      });
      setChargeRows(
        (voucher.chargeLines ?? []).map((cl, idx) => ({
          key: cl.id ?? `cl-${idx}`,
          charge_type: cl.charge_type,
          account_id: cl.account_id,
          amount: Number(cl.amount),
          currency: cl.currency,
          tax_code: cl.tax_code,
          tax_amount: Number(cl.tax_amount ?? 0),
          remarks: cl.remarks,
        })),
      );
    }
  }, [voucher, isEdit, form, navigate, message, t]);

  useEffect(() => {
    if (isNew && !exposureId && sarIdParam) {
      supplierAdvanceRefundService
        .findAllExposures({ purchase_order_id: undefined })
        .catch(() => [])
        .then(() =>
          supplierAdvanceRefundService.findAllExposures().then((list) => {
            const match = list.find(
              (e) => e.supplier_advance_request_id === sarIdParam,
            );
            if (match) applyExposureContext(match);
            else {
              setContext((prev) => ({
                ...prev,
                supplier_advance_request_id: sarIdParam,
                original_advance: 0,
                already_refunded: 0,
                already_adjusted: 0,
                refund_available: 0,
                advance_currency: baseCurrency?.currency_code ?? "AED",
              }));
              form.setFieldsValue({
                refund_date: dayjs(),
                advance_currency: baseCurrency?.currency_code ?? "AED",
                refund_currency: baseCurrency?.currency_code ?? "AED",
                advance_exchange_rate: 1,
                refund_exchange_rate: 1,
              });
            }
          }),
        );
    }
  }, [isNew, exposureId, sarIdParam, baseCurrency?.currency_code]);

  useEffect(() => {
    if (isNew && !exposureId && !sarIdParam && !context) {
      form.setFieldsValue({
        refund_date: dayjs(),
        advance_currency: baseCurrency?.currency_code ?? "AED",
        refund_currency: baseCurrency?.currency_code ?? "AED",
        advance_exchange_rate: 1,
        refund_exchange_rate: 1,
      });
    }
  }, [
    isNew,
    exposureId,
    sarIdParam,
    context,
    baseCurrency?.currency_code,
    form,
  ]);

  const saveMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      const payload: CreateSupplierAdvanceRefundPayload = {
        recovery_exposure_id: context?.recovery_exposure_id,
        supplier_advance_request_id:
          context?.supplier_advance_request_id ?? sarIdParam ?? undefined,
        refund_date: (values.refund_date as dayjs.Dayjs).format("YYYY-MM-DD"),
        refund_amount: Number(values.refund_amount),
        bank_received_amount: Number(values.bank_received_amount),
        receiving_account_id: values.receiving_account_id as string,
        advance_gl_account_id: values.advance_gl_account_id as string,
        advance_currency: values.advance_currency as string,
        refund_currency: values.refund_currency as string,
        advance_exchange_rate: Number(values.advance_exchange_rate ?? 1),
        refund_exchange_rate: Number(values.refund_exchange_rate ?? 1),
        exchange_gain_loss_account_id: values.exchange_gain_loss_account_id as
          | string
          | undefined,
        reference_number: values.reference_number as string | undefined,
        narration: values.narration as string | undefined,
        charge_lines: chargeRows
          .filter((r) => Number(r.amount) > 0 && r.account_id)
          .map(
            ({
              charge_type,
              account_id,
              amount,
              currency,
              tax_code,
              tax_amount,
              remarks,
            }) => ({
              charge_type,
              account_id,
              amount: Number(amount),
              currency: currency ?? refundCurrency,
              tax_code,
              tax_amount: Number(tax_amount ?? 0),
              remarks,
            }),
          ),
      };
      if (isEdit && id) {
        return supplierAdvanceRefundService.update(id, payload);
      }
      return supplierAdvanceRefundService.create(payload);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["supplier-advance-refunds"] });
      message.success(
        isEdit ? t("refundVoucherUpdated") : t("refundVoucherCreated"),
      );
      navigate(`${LIST_PATH}/${saved.id}`);
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message || t("saveFailed"));
    },
  });

  const addChargeRow = () => {
    setChargeRows((rows) => [
      ...rows,
      {
        key: `new-${Date.now()}`,
        charge_type: "Bank Charges",
        account_id: "",
        amount: 0,
        currency: refundCurrency ?? context?.advance_currency ?? "AED",
        tax_amount: 0,
      },
    ]);
  };

  const chargeColumns: ColumnsType<ChargeRow> = [
    {
      title: t("chargeType"),
      dataIndex: "charge_type",
      width: 180,
      render: (val, record) => (
        <Select
          style={{ width: "100%" }}
          value={val}
          options={REFUND_CHARGE_TYPES.map((t) => ({ value: t, label: t }))}
          onChange={(v) =>
            setChargeRows((rows) =>
              rows.map((r) =>
                r.key === record.key ? { ...r, charge_type: v } : r,
              ),
            )
          }
        />
      ),
    },
    {
      title: t("account"),
      dataIndex: "account_id",
      width: 220,
      render: (val, record) => (
        <Select
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          placeholder={t("account")}
          value={val || undefined}
          options={chargeAccountOptions}
          onChange={(v) =>
            setChargeRows((rows) =>
              rows.map((r) =>
                r.key === record.key ? { ...r, account_id: v } : r,
              ),
            )
          }
        />
      ),
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      width: 120,
      render: (val, record) => (
        <InputNumber
          min={0}
          precision={2}
          style={{ width: "100%" }}
          value={val}
          onChange={(v) =>
            setChargeRows((rows) =>
              rows.map((r) =>
                r.key === record.key ? { ...r, amount: Number(v ?? 0) } : r,
              ),
            )
          }
        />
      ),
    },
    {
      title: t("remarks"),
      dataIndex: "remarks",
      render: (val, record) => (
        <Input
          value={val}
          onChange={(e) =>
            setChargeRows((rows) =>
              rows.map((r) =>
                r.key === record.key ? { ...r, remarks: e.target.value } : r,
              ),
            )
          }
        />
      ),
    },
    {
      title: "",
      key: "del",
      width: 48,
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() =>
            setChargeRows((rows) => rows.filter((r) => r.key !== record.key))
          }
        />
      ),
    },
  ];

  if ((isEdit && voucherLoading) || (isNew && exposureId && exposureLoading)) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  const remainingAfter = context
    ? Math.max(0, context.refund_available - (Number(refundAmount) || 0))
    : null;

  return (
    <div style={{ padding: 24 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(LIST_PATH)}
        style={{ marginBottom: 8 }}
      >
        {t("backToList")}
      </Button>
      <Title level={4}>
        {isEdit
          ? voucher?.voucher_number
            ? t("editRefundTitle", { number: voucher.voucher_number })
            : t("editRefundFallback")
          : t("newSupplierAdvanceRefund")}
      </Title>

      {context && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={4}>
            <Descriptions.Item label={t("supplier")}>
              {context.supplier_name ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("po")}>
              {context.po_number ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("advanceRequest")}>
              {context.advance_request_number ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label={t("currency")}>
              {context.advance_currency}
            </Descriptions.Item>
            <Descriptions.Item label={t("originalAdvance")}>
              {Number(context.original_advance).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label={t("alreadyRefunded")}>
              {Number(context.already_refunded).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label={t("alreadyAdjusted")}>
              {Number(context.already_adjusted).toFixed(2)}
            </Descriptions.Item>
            <Descriptions.Item label={t("refundAvailable")}>
              <Text strong>{Number(context.refund_available).toFixed(2)}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {!context?.recovery_exposure_id &&
        !context?.supplier_advance_request_id &&
        isNew && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t("selectRecoveryExposure")}
            description={t("selectRecoveryExposureDesc")}
          />
        )}

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => saveMutation.mutate(values)}
      >
        <Card title={t("voucherHeader")} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="refund_date"
                label={t("refundDate")}
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="reference_number"
                label={t("bankRefundReference")}
              >
                <Input placeholder={t("bankRefundReferencePlaceholder")} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="receiving_account_id"
                label={t("receivingBankAccount")}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={bankAccountOptions}
                  placeholder={t("receivingBankAccount")}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="advance_gl_account_id"
                label={t("supplierAdvanceGlAccount")}
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={advanceGlOptions}
                  placeholder={t("advanceAccountPlaceholder")}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="narration" label={t("narration")}>
                <TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title={t("refundAmounts")} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="advance_currency" label={t("advanceCurrency")}>
                <Select loading={currenciesLoading} options={currencyOptions} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="refund_currency" label={t("refundCurrency")}>
                <Select loading={currenciesLoading} options={currencyOptions} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="refund_amount"
                label={t("refundAmountAdvanceCcy")}
                rules={[{ required: true, message: t("required") }]}
                extra={
                  remainingAfter != null
                    ? `${t("remainingAfter")}: ${remainingAfter.toFixed(2)}`
                    : undefined
                }
              >
                <InputNumber min={0} precision={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="bank_received_amount"
                label={t("bankReceivedRefundCcy")}
                rules={[{ required: true, message: t("required") }]}
                extra={
                  chargeTotal > 0
                    ? t("mustCoverCharges", { amount: chargeTotal.toFixed(2) })
                    : undefined
                }
              >
                <InputNumber min={0} precision={2} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title={t("chargesDeductions")}
          style={{ marginBottom: 16 }}
          extra={
            <Button icon={<PlusOutlined />} onClick={addChargeRow}>
              {t("addCharge")}
            </Button>
          }
        >
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={chargeRows}
            columns={chargeColumns}
            locale={{ emptyText: t("noChargesHint") }}
          />
          {chargeTotal > 0 && (
            <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
              {t("chargeTotalHint", { amount: chargeTotal.toFixed(2) })}
            </Text>
          )}
        </Card>

        <Card title={t("exchangePreview")} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="advance_exchange_rate" label={t("advanceRate")}>
                <InputNumber min={0} precision={6} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="refund_exchange_rate" label={t("refundRate")}>
                <InputNumber min={0} precision={6} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="exchange_gain_loss_account_id"
                label={t("exchangeGainLossAccount")}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={fxGainLossOptions}
                  placeholder={t("requiredIfFxDifference")}
                />
              </Form.Item>
            </Col>
          </Row>
          {fxPreview && (
            <Alert
              type={
                Math.abs(fxPreview.exchangeDifference) > 0.01
                  ? "warning"
                  : "info"
              }
              showIcon
              message={t("exchangeDifferenceBreakdown")}
              description={
                <Space direction="vertical" size={0}>
                  <span>
                    {t("advanceCarryingValueBase", {
                      amount: fxPreview.advanceBase.toFixed(2),
                    })}
                  </span>
                  <span>
                    {t("refundBaseValue", {
                      amount: fxPreview.refundBase.toFixed(2),
                    })}
                  </span>
                  <span>
                    {t("exchangeDifferenceAmount")}{" "}
                    <Text
                      strong
                      type={
                        fxPreview.exchangeDifference < 0 ? "danger" : "success"
                      }
                    >
                      {fxPreview.exchangeDifference.toFixed(2)}
                    </Text>{" "}
                    {fxPreview.exchangeDifference > 0
                      ? t("gainParen")
                      : fxPreview.exchangeDifference < 0
                        ? t("lossParen")
                        : ""}
                  </span>
                </Space>
              }
            />
          )}
        </Card>

        <Divider />
        {isEdit && id ? (
          <EntityAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_ADVANCE_REFUND_VOUCHER}
            entityId={id}
            title={tc("attachments")}
            queryKeyPrefix={["documents", "supplier-advance-refund"]}
            uploadHint={SCM_ATTACHMENT_UPLOAD_HINT}
          />
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t("attachmentsAfterSave")}
          />
        )}
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SaveOutlined />}
            loading={saveMutation.isPending}
          >
            {isEdit ? t("saveChanges") : t("createDraft")}
          </Button>
          <Button onClick={() => navigate(LIST_PATH)}>{tc("cancel")}</Button>
        </Space>
      </Form>
    </div>
  );
};

export default SupplierAdvanceRefundFormPage;
